import type {
    RemoveFavoriteFailedActionMessage,
    RemoveFavoriteCompletedActionMessage,
    RemoveFavoriteFromStoreActionMessage,
} from './actions/v2/removeFavoriteActions';
import type {
    AddMultipleFavoritesCompletedActionMessage,
    AddFavoriteFailedActionMessage,
    AddFavoriteCompletedActionMessage,
    AddFavoriteToStoreActionMessage,
} from './actions/v2/addFavoriteActions';
import type { ADD_FAVORITE_GROUPActionMessage } from './actions/v2/addFavoriteGroup';
import type { REMOVE_FAVORITE_GROUPActionMessage } from './actions/v2/removeFavoriteGroup';
import type { AddFavoritePersonaV2ActionMessage } from './actions/v2/people/addFavoritePersonaV2';
import type { RemoveFavoritePersonaV2ActionMessage } from './actions/v2/people/removeFavoritePersonaV2';
import type { ADD_NEW_MAILBOX_DEFAULT_FAVORITESActionMessage } from './actions/v2/addNewMailboxDefaultFavorites';
import type { REMOVE_FAVORITE_CATEGORYActionMessage } from './actions/removeFavoriteCategory';
import type { ADD_FAVORITE_CATEGORYActionMessage } from './actions/addFavoriteCategory';
import type { MailboxInfo } from 'owa-client-types';
import type { UPDATE_FAVORITE_POSITIONActionMessage } from './actions/updateFavoritePosition';
import type { MOVE_FAVORITE_UP_DOWNActionMessage } from './actions/moveFavoriteUpDown';
import type {
    FavoriteOperationError,
    FavoriteData,
    FolderForestNodeType,
} from 'owa-favorites-types';
import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';

const lazyModule = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "FavoritesStore"*/ './lazyIndex'),
    { name: 'FavoritesStore' }
);

export const lazyMoveFavoriteUpDown: LazyImport<
    (
        favoriteId: string,
        isMoveUp: boolean,
        favoriteType: FolderForestNodeType
    ) => MOVE_FAVORITE_UP_DOWNActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.moveFavoriteUpDown);
export const lazyUpdateFavoritePosition: LazyImport<
    (
        favoriteIdToUpdate: string,
        destinationFavoriteId: string,
        favoriteType: FolderForestNodeType
    ) => UPDATE_FAVORITE_POSITIONActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.updateFavoritePosition);
export const lazyAddFavoriteCategory: LazyImport<
    (
        categoryId: string,
        actionSource: string,
        mailboxInfo: MailboxInfo
    ) => ADD_FAVORITE_CATEGORYActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteCategory);
export const lazyRemoveFavoriteCategory: LazyImport<
    (categoryId: string, actionSource: string) => REMOVE_FAVORITE_CATEGORYActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoriteCategory);
export const lazyAddNewMailboxDefaultFavorites: LazyAction<
    (mailboxInfo: MailboxInfo) => ADD_NEW_MAILBOX_DEFAULT_FAVORITESActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.addNewMailboxDefaultFavorites);
export const lazyRemoveFavoritePersonaV2: LazyImport<
    (
        favoriteId: string,
        actionSource: string,
        skipOfflineResolvers?: boolean
    ) => RemoveFavoritePersonaV2ActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoritePersonaV2);
export const lazyAddFavoritePersonaV2: LazyImport<
    (
        emailAddress: string,
        displayName: string,
        actionSource: string,
        mailboxInfo?: MailboxInfo,
        skipOfflineResolvers?: boolean
    ) => AddFavoritePersonaV2ActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoritePersonaV2);

export const lazyRemoveFavoriteGroup: LazyImport<
    (
        groupIdToRemove: string,
        actionSource: string,
        mailboxInfo: MailboxInfo,
        externalRequest?: boolean,
        favoriteId?: string,
        onServiceCallCompleted?: (succeeded: boolean) => void
    ) => REMOVE_FAVORITE_GROUPActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoriteGroup);
export const lazyAddFavoriteGroup: LazyImport<
    (
        groupSmtp: string,
        displayName: string,
        actionSource: string,
        mailboxInfo: MailboxInfo,
        externalRequest?: boolean,
        onServiceCallCompleted?: (succeeded: boolean, favoriteData?: FavoriteData) => void
    ) => ADD_FAVORITE_GROUPActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteGroup);

export const lazyRemoveFavoriteFolder: LazyImport<
    typeof lazyIndex.removeFavoriteFolder,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoriteFolder);
export const lazyAddFavoriteFolder: LazyImport<
    typeof lazyIndex.addFavoriteFolder,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteFolder);

export const lazyRemoveFavoritePlaceholder: LazyImport<
    typeof lazyIndex.removeFavoritePlaceholder,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoritePlaceholder);

export const lazySetOutlookFavoritesBitFlag: LazyAction<
    typeof lazyIndex.setOutlookFavoritesBitFlag,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setOutlookFavoritesBitFlag);
// Favoriting actions
export const lazyAddFavoriteToStore: LazyImport<
    (favoriteData: FavoriteData, newIndex?: number) => AddFavoriteToStoreActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteToStore);
export const lazyAddFavoriteCompleted: LazyImport<
    (
        temporaryData: FavoriteData,
        favoriteData: FavoriteData,
        newIndex?: number
    ) => AddFavoriteCompletedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteCompleted);
export const lazyAddFavoriteFailed: LazyImport<
    (error: FavoriteOperationError, favoriteData: FavoriteData) => AddFavoriteFailedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addFavoriteFailed);

export const lazyAddMultipleFavoritesCompleted: LazyImport<
    (
        temporaryFavorites: FavoriteData[],
        favoritesData: FavoriteData[]
    ) => AddMultipleFavoritesCompletedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.addMultipleFavoritesCompleted);

export const lazyRemoveFavoriteFromStore: LazyImport<
    (favoriteData: FavoriteData) => RemoveFavoriteFromStoreActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoriteFromStore);
export const lazyRemoveFavoriteCompleted: LazyImport<
    (favoriteData: FavoriteData) => RemoveFavoriteCompletedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.removeFavoriteCompleted);
export const lazyRemoveFavoriteFailed: LazyAction<
    (
        error: FavoriteOperationError,
        favoriteData: FavoriteData
    ) => RemoveFavoriteFailedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeFavoriteFailed);
export const lazyEditOutlookFavoriteService: LazyAction<
    typeof lazyIndex.editOutlookFavoriteService,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.editOutlookFavoriteService);
export const lazyCreateOutlookFavoriteService: LazyAction<
    typeof lazyIndex.createOutlookFavoriteService,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.createOutlookFavoriteService);
export const lazyDeleteOutlookFavoriteService: LazyAction<
    typeof lazyIndex.removeOutlookFavoriteHelper,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeOutlookFavoriteHelper);
export const lazyCreateMultipleOutlookFavoritesService: LazyAction<
    typeof lazyIndex.createMultipleOutlookFavoritesService,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.createMultipleOutlookFavoritesService);
export const lazyGetOutlookFavoritesService: LazyAction<
    typeof lazyIndex.getOutlookFavoritesService,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getOutlookFavoritesService);
